import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Help as HelpIcon,
  Search as SearchIcon,
  QuestionAnswer as QuestionAnswerIcon,
  VideoLibrary as VideoLibraryIcon,
  Description as DescriptionIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Chat as ChatIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Calculate as CalculateIcon,
  AttachMoney as AttachMoneyIcon,
  Security as SecurityIcon,
  BugReport as BugReportIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  PlayArrow as PlayArrowIcon,
  Download as DownloadIcon
} from '@mui/icons-material';

const HelpCenter = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  const categories = [
    { id: 'all', label: 'All Topics', icon: <HelpIcon /> },
    { id: 'calculator', label: 'Calculators', icon: <CalculateIcon /> },
    { id: 'premium', label: 'Premium & Pricing', icon: <AttachMoneyIcon /> },
    { id: 'claims', label: 'Claims Process', icon: <AssessmentIcon /> },
    { id: 'perils', label: 'Perils & Diseases', icon: <BugReportIcon /> },
    { id: 'policy', label: 'Policy Terms', icon: <SecurityIcon /> },
    { id: 'technical', label: 'Technical Support', icon: <TimelineIcon /> }
  ];

  const faqs = [
    {
      id: 1,
      category: 'calculator',
      question: 'How do I calculate my claim payout?',
      answer: 'Use the Claim Calculator by entering your species, DOC period, day of loss, and stocking density. The calculator will automatically apply all policy rules including the 70% total loss threshold and 80% indemnity rate.',
      steps: [
        'Select your shrimp species (P. vannamei, P. monodon, etc.)',
        'Choose your DOC period (60, 80, 100, 120, or 130 days)',
        'Enter the day when total loss occurred',
        'Input your actual stocking density per m²',
        'Click "Calculate Claim" to get detailed results'
      ]
    },
    {
      id: 2,
      category: 'premium',
      question: 'How is my insurance premium calculated?',
      answer: 'Your premium is calculated based on your chosen plan, species, DOC period, and sum insured. Discounts and loadings are then applied based on your farm conditions and practices.',
      steps: [
        'Base premium rate is determined by your plan and DOC period',
        'Apply available discounts (up to 40% total)',
        'Apply applicable loadings (up to 30% total)',
        'Calculate net premium after discounts/loadings',
        'Add 18% GST to get final premium'
      ]
    },
    {
      id: 3,
      category: 'claims',
      question: 'What is the 70% total loss threshold?',
      answer: 'The 70% total loss threshold means that claims are only admissible when the loss percentage is at least 70% of your total stock. This is a policy requirement to ensure only significant losses are covered.',
      steps: [
        'Calculate your actual loss percentage',
        'Compare with 70% minimum threshold',
        'If below 70%, claim is not admissible',
        'If 70% or above, proceed with claim calculation',
        'Apply 80% indemnity rate to final payout'
      ]
    },
    {
      id: 4,
      category: 'perils',
      question: 'What perils are covered under my insurance?',
      answer: 'Coverage depends on your plan. Plan A covers 11 basic perils, Plan B adds 7 notified diseases, and Plan C includes additional diseases. Check the Perils & Diseases section for complete details.',
      steps: [
        'Plan A: 11 basic perils (natural disasters, accidents, etc.)',
        'Plan B: All Plan A perils + 7 notified diseases',
        'Plan C: All Plan B coverage + additional diseases',
        'Review specific perils in the Perils Information section',
        'Contact support if you need clarification on coverage'
      ]
    },
    {
      id: 5,
      category: 'policy',
      question: 'What discounts are available for my premium?',
      answer: 'Several discounts are available including good farm management (5%), SPF seed selection (15%), soil testing (5%), water reservoir (5%), epidemic-free history (10%), favorable soil type (10%), and optimal stocking density (10%). Total discounts are capped at 40%.',
      steps: [
        'Good Farm Management: 5% discount',
        'SPF/SPR Seed Selection: 15% discount',
        'Soil & Water Testing: 5% discount',
        'Water Reservoir Pond: 5% discount',
        'Epidemic-Free History: 10% discount',
        'Favorable Soil Type: 10% discount',
        'Optimal Stocking Density: 10% discount'
      ]
    },
    {
      id: 6,
      category: 'technical',
      question: 'How do I contact technical support?',
      answer: 'You can contact our technical support team through multiple channels. We provide 24/7 support for urgent issues and business hours support for general inquiries.',
      steps: [
        'Phone: +91-XXX-XXXX-XXXX (24/7 for urgent issues)',
        'Email: support@piscicare.com (response within 4 hours)',
        'Live Chat: Available during business hours',
        'Help Center: Search our comprehensive FAQ database',
        'Video Tutorials: Watch step-by-step guides'
      ]
    }
  ];

  const videoTutorials = [
    {
      id: 1,
      title: 'How to Use the Claim Calculator',
      description: 'Step-by-step guide to calculating your claim payout',
      duration: '5:30',
      category: 'calculator',
      thumbnail: '/api/placeholder/300/200'
    },
    {
      id: 2,
      title: 'Premium Calculator Tutorial',
      description: 'Learn how to calculate your insurance premium with discounts',
      duration: '7:15',
      category: 'premium',
      thumbnail: '/api/placeholder/300/200'
    },
    {
      id: 3,
      title: 'Understanding Policy Terms',
      description: 'Complete guide to insurance policy terms and conditions',
      duration: '12:45',
      category: 'policy',
      thumbnail: '/api/placeholder/300/200'
    },
    {
      id: 4,
      title: 'Perils and Diseases Coverage',
      description: 'What is covered under different insurance plans',
      duration: '8:20',
      category: 'perils',
      thumbnail: '/api/placeholder/300/200'
    }
  ];

  const filteredFaqs = faqs.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const filteredVideos = videoTutorials.filter(video => {
    const matchesSearch = video.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         video.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || video.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleVideoClick = (video) => {
    setSelectedVideo(video);
    setVideoDialogOpen(true);
  };

  const TabPanel = ({ children, value, index, ...other }) => (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`help-tabpanel-${index}`}
      aria-labelledby={`help-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <Box p={3}>
      {/* Header */}
      <Paper elevation={3} sx={{ 
        p: 4, 
        mb: 4, 
        background: 'linear-gradient(135deg, #7b1fa2 0%, #4a148c 100%)',
        borderRadius: 2,
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
      }}>
        <Box display="flex" alignItems="center" mb={2}>
          <Box
            sx={{
              p: 2,
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.2)',
              mr: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <HelpIcon sx={{ fontSize: 32, color: 'white' }} />
          </Box>
          <Box>
            <Typography variant="h4" component="h1" color="white" fontWeight="bold" gutterBottom>
              Help Center
            </Typography>
            <Typography variant="h6" color="white" sx={{ opacity: 0.9, fontWeight: 400 }}>
              Comprehensive support for calculators, policies, and insurance processes
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Search Bar */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search help articles, FAQs, and tutorials..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Category Filter */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Browse by Category
        </Typography>
        <Grid container spacing={2}>
          {categories.map((category) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={category.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  border: selectedCategory === category.id ? 2 : 1,
                  borderColor: selectedCategory === category.id ? 'primary.main' : 'divider',
                  '&:hover': { borderColor: 'primary.main' }
                }}
                onClick={() => setSelectedCategory(category.id)}
              >
                <CardContent sx={{ textAlign: 'center', p: 2 }}>
                  {category.icon}
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {category.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Main Content Tabs */}
      <Paper elevation={2}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
        >
          <Tab label="FAQs" icon={<QuestionAnswerIcon />} />
          <Tab label="Video Tutorials" icon={<VideoLibraryIcon />} />
          <Tab label="Documentation" icon={<DescriptionIcon />} />
          <Tab label="Contact Support" icon={<PhoneIcon />} />
        </Tabs>

        {/* FAQs Tab */}
        <TabPanel value={activeTab} index={0}>
          <Typography variant="h5" gutterBottom>
            Frequently Asked Questions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {filteredFaqs.length} questions found
          </Typography>

          {filteredFaqs.map((faq) => (
            <Accordion key={faq.id} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" width="100%">
                  <Chip
                    label={categories.find(c => c.id === faq.category)?.label}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ mr: 2 }}
                  />
                  <Typography variant="h6">{faq.question}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" paragraph>
                  {faq.answer}
                </Typography>
                {faq.steps && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Step-by-Step Guide:
                    </Typography>
                    <Stepper orientation="vertical">
                      {faq.steps.map((step, index) => (
                        <Step key={index} active>
                          <StepLabel>{step}</StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          ))}

          {filteredFaqs.length === 0 && (
            <Alert severity="info">
              No FAQs found matching your search criteria. Try adjusting your search terms or category filter.
            </Alert>
          )}
        </TabPanel>

        {/* Video Tutorials Tab */}
        <TabPanel value={activeTab} index={1}>
          <Typography variant="h5" gutterBottom>
            Video Tutorials
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {filteredVideos.length} videos available
          </Typography>

          <Grid container spacing={3}>
            {filteredVideos.map((video) => (
              <Grid item xs={12} sm={6} md={4} key={video.id}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 }
                  }}
                  onClick={() => handleVideoClick(video)}
                >
                  <Box
                    sx={{
                      height: 200,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }}
                  >
                    <PlayArrowIcon sx={{ fontSize: 60, color: 'white' }} />
                    <Chip
                      label={video.duration}
                      size="small"
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        right: 8,
                        bgcolor: 'rgba(0,0,0,0.7)',
                        color: 'white'
                      }}
                    />
                  </Box>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {video.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {video.description}
                    </Typography>
                    <Chip
                      label={categories.find(c => c.id === video.category)?.label}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ mt: 1 }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {filteredVideos.length === 0 && (
            <Alert severity="info">
              No video tutorials found matching your search criteria.
            </Alert>
          )}
        </TabPanel>

        {/* Documentation Tab */}
        <TabPanel value={activeTab} index={2}>
          <Typography variant="h5" gutterBottom>
            Documentation & Guides
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <CalculateIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Calculator Guides
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon><DescriptionIcon /></ListItemIcon>
                      <ListItemText
                        primary="Claim Calculator User Guide"
                        secondary="Complete guide to using the claim calculator"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><DescriptionIcon /></ListItemIcon>
                      <ListItemText
                        primary="Premium Calculator Manual"
                        secondary="How to calculate premiums with discounts"
                      />
                    </ListItem>
                  </List>
                  <Button variant="outlined" startIcon={<DownloadIcon />}>
                    Download All Guides
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Policy Documentation
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon><DescriptionIcon /></ListItemIcon>
                      <ListItemText
                        primary="Policy Terms & Conditions"
                        secondary="Complete policy documentation"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><DescriptionIcon /></ListItemIcon>
                      <ListItemText
                        primary="Perils & Diseases Coverage"
                        secondary="Detailed coverage information"
                      />
                    </ListItem>
                  </List>
                  <Button variant="outlined" startIcon={<DownloadIcon />}>
                    Download Policy Docs
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Contact Support Tab */}
        <TabPanel value={activeTab} index={3}>
          <Typography variant="h5" gutterBottom>
            Contact Support
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card sx={{ textAlign: 'center', p: 3 }}>
                <PhoneIcon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Phone Support
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Available 24/7 for urgent issues
                </Typography>
                <Typography variant="h6" color="primary">
                  +91-XXX-XXXX-XXXX
                </Typography>
                <Button variant="contained" sx={{ mt: 2 }}>
                  Call Now
                </Button>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ textAlign: 'center', p: 3 }}>
                <EmailIcon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Email Support
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Response within 4 hours
                </Typography>
                <Typography variant="h6" color="primary">
                  support@piscicare.com
                </Typography>
                <Button variant="contained" sx={{ mt: 2 }}>
                  Send Email
                </Button>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ textAlign: 'center', p: 3 }}>
                <ChatIcon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Live Chat
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Available during business hours
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  9 AM - 6 PM (IST)
                </Typography>
                <Button variant="contained" sx={{ mt: 2 }}>
                  Start Chat
                </Button>
              </Card>
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Alert severity="info">
            <Typography variant="h6" gutterBottom>
              Support Hours
            </Typography>
            <Typography variant="body2">
              <strong>Phone Support:</strong> 24/7 for urgent issues<br />
              <strong>Email Support:</strong> 9 AM - 6 PM (IST), Monday to Friday<br />
              <strong>Live Chat:</strong> 9 AM - 6 PM (IST), Monday to Friday<br />
              <strong>Response Time:</strong> Email within 4 hours, Live Chat immediate
            </Typography>
          </Alert>
        </TabPanel>
      </Paper>

      {/* Video Dialog */}
      <Dialog
        open={videoDialogOpen}
        onClose={() => setVideoDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">{selectedVideo?.title}</Typography>
            <IconButton onClick={() => setVideoDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedVideo && (
            <Box>
              <Box
                sx={{
                  height: 400,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2
                }}
              >
                <PlayArrowIcon sx={{ fontSize: 80, color: 'white' }} />
              </Box>
              <Typography variant="body1" paragraph>
                {selectedVideo.description}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Duration: {selectedVideo.duration}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVideoDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HelpCenter;
